<template>
  <transition name="feedback-toast">
    <aside
      v-if="isVisible"
      class="feedback-toast-card"
      role="dialog"
      aria-labelledby="feedback-toast-title"
      :aria-describedby="submitted ? 'feedback-success-message' : 'feedback-toast-question'"
    >
      <button
        type="button"
        class="feedback-toast-close"
        :aria-label="$t('close')"
        :disabled="savingPreference || submitting"
        @click="dismiss"
      >
        <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
      </button>

      <div v-if="submitted" class="feedback-success" role="status">
        <span class="feedback-success-icon">
          <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" />
        </span>
        <h2 id="feedback-toast-title">{{ $t('feedback_thank_you') }}</h2>
        <p id="feedback-success-message">{{ $t('feedback_success_message') }}</p>
      </div>

      <form v-else @submit.prevent="submitFeedback">
        <div class="feedback-heading">
          <span class="feedback-heading-icon">
            <AppIcon name="bi-chat-heart-fill" class="bi bi-chat-heart-fill" aria-hidden="true" />
          </span>
          <div>
            <span class="feedback-eyebrow">{{ $t('feedback_title') }}</span>
            <h2 id="feedback-toast-title">{{ $t('feedback_question') }}</h2>
          </div>
        </div>

        <p id="feedback-toast-question" class="feedback-helper">
          {{ $t('feedback_rating_helper') }}
        </p>

        <div
          class="feedback-stars"
          role="radiogroup"
          :aria-label="$t('feedback_rating_label')"
          @mouseleave="hoveredRating = 0"
        >
          <button
            v-for="star in 5"
            :key="star"
            type="button"
            class="feedback-star"
            :class="{ 'is-active': star <= displayedRating }"
            role="radio"
            :aria-checked="rating === star ? 'true' : 'false'"
            :aria-label="$t('feedback_star_label', { count: star })"
            @mouseenter="hoveredRating = star"
            @focus="hoveredRating = star"
            @blur="hoveredRating = 0"
            @click="rating = star"
          >
            <AppIcon :name="star <= displayedRating ? 'bi-star-fill' : 'bi-star'"
              class="bi"
              :class="star <= displayedRating ? 'bi-star-fill' : 'bi-star'"
              aria-hidden="true"
             />
          </button>
        </div>

        <label class="feedback-label" for="feedback-message">
          {{ $t('feedback_or_suggestions') }}
          <span>{{ $t('feedback_optional') }}</span>
        </label>
        <textarea
          id="feedback-message"
          v-model.trim="message"
          class="feedback-textarea"
          rows="3"
          maxlength="1000"
          :placeholder="$t('feedback_placeholder')"
        ></textarea>

        <p v-if="errorMessage" class="feedback-error" role="alert">
          <AppIcon name="bi-exclamation-circle" class="bi bi-exclamation-circle" aria-hidden="true" />
          {{ errorMessage }}
        </p>

        <button
          type="submit"
          class="feedback-submit"
          :disabled="!rating || submitting"
        >
          <span v-if="submitting" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          <span>{{ submitting ? $t('feedback_sending') : $t('feedback_submit') }}</span>
          <AppIcon :name="$i18n.locale === 'ar' ? 'bi-arrow-left' : 'bi-arrow-right'"
            v-if="!submitting"
            class="bi"
            :class="$i18n.locale === 'ar' ? 'bi-arrow-left' : 'bi-arrow-right'"
            aria-hidden="true"
           />
        </button>
      </form>
    </aside>
  </transition>
</template>

<script>
import helper from '../helper'

export default {
  name: 'FeedbackToast',
  data() {
    return {
      isVisible: false,
      rating: 0,
      hoveredRating: 0,
      message: '',
      submitting: false,
      submitted: false,
      savingPreference: false,
      errorMessage: '',
      showTimer: null,
      dismissTimer: null,
    }
  },
  computed: {
    displayedRating() {
      return this.hoveredRating || this.rating
    },
    business() {
      return this.$store.getters.BUSINESS || {}
    },
    feedbackSettings() {
      return this.business.metadata?.feedback_toast || {}
    },
    feedbackAlreadyHandled() {
      return Boolean(this.feedbackSettings.dismissed_at || this.feedbackSettings.submitted_at)
    },
  },
  mounted() {
    if (this.feedbackAlreadyHandled) return

    this.showTimer = window.setTimeout(() => {
      this.isVisible = true
    }, 700)
  },
  beforeDestroy() {
    window.clearTimeout(this.showTimer)
    window.clearTimeout(this.dismissTimer)
  },
  methods: {
    syncFeedbackSettings(feedbackSettings) {
      const business = {
        ...this.business,
        metadata: {
          ...(this.business.metadata || {}),
          feedback_toast: feedbackSettings,
        },
      }

      this.$store.commit('SET_BUSINESS', business)
    },
    async dismiss() {
      if (this.savingPreference || this.submitting) return

      this.savingPreference = true
      this.errorMessage = ''

      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/feedback/dismiss`)
        this.syncFeedbackSettings(response.data.feedback_settings)
        this.isVisible = false
      } catch (error) {
        this.errorMessage = this.$t('feedback_preference_error_message')
      } finally {
        this.savingPreference = false
      }
    },
    async submitFeedback() {
      if (!this.rating || this.submitting) return

      this.submitting = true
      this.errorMessage = ''

      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/feedback`, {
          rating: this.rating,
          message: this.message,
          path: this.$route.fullPath,
          locale: this.$i18n.locale,
        })

        this.syncFeedbackSettings(response.data.feedback_settings)
        this.submitted = true
        this.dismissTimer = window.setTimeout(() => {
          this.isVisible = false
        }, 2800)
      } catch (error) {
        this.errorMessage = this.$t('feedback_error_message')
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.feedback-toast-card {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: var(--notification-layer, 100000000);
  width: min(380px, calc(100vw - 32px));
  padding: 24px;
  color: #172033;
  text-align: start;
  background: #ffffff;
  border: 1px solid #e7eaf0;
  border-radius: 18px;
  box-shadow: 0 22px 60px rgba(24, 33, 51, 0.18), 0 4px 14px rgba(24, 33, 51, 0.08);
}

.feedback-toast-close {
  position: absolute;
  inset-block-start: 16px;
  inset-inline-end: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #7a8495;
  background: #f5f6f8;
  border: 0;
  border-radius: 50%;
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

.feedback-toast-close:hover {
  color: #172033;
  background: #e9ecf1;
  transform: rotate(6deg);
}

.feedback-toast-close:focus-visible,
.feedback-star:focus-visible,
.feedback-submit:focus-visible,
.feedback-textarea:focus-visible {
  outline: 3px solid rgba(68, 90, 237, 0.22);
  outline-offset: 2px;
}

.feedback-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  padding-inline-end: 30px;
}

.feedback-heading-icon,
.feedback-success-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: #445aed;
  font-size: 20px;
  background: #eef0ff;
  border-radius: 13px;
}

.feedback-eyebrow {
  display: block;
  margin-bottom: 2px;
  color: #788398;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  line-height: 1.2;
  text-transform: uppercase;
}

.feedback-heading h2,
.feedback-success h2 {
  margin: 0;
  color: #172033;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
}

.feedback-helper {
  margin: 18px 0 8px;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}

.feedback-stars {
  display: flex;
  gap: 7px;
  margin-bottom: 18px;
}

.feedback-star {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 43px;
  height: 43px;
  padding: 0;
  color: #c9cfda;
  font-size: 25px;
  background: #fafbfc;
  border: 1px solid #e7eaf0;
  border-radius: 11px;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
}

.feedback-star:hover,
.feedback-star.is-active {
  color: #f5ad22;
  background: #fff9e9;
  border-color: #f6d78e;
  transform: translateY(-2px);
}

.feedback-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
  color: #344054;
  font-size: 13px;
  font-weight: 700;
}

.feedback-label span {
  color: #98a2b3;
  font-size: 11px;
  font-weight: 500;
}

.feedback-textarea {
  display: block;
  width: 100%;
  min-height: 88px;
  padding: 11px 12px;
  color: #172033;
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  background: #fafbfc;
  border: 1px solid #dde2ea;
  border-radius: 11px;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.feedback-textarea::placeholder {
  color: #a0a8b8;
}

.feedback-textarea:focus {
  background: #ffffff;
  border-color: #7c8cf4;
  box-shadow: 0 0 0 3px rgba(68, 90, 237, 0.1);
}

.feedback-error {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 10px 0 0;
  color: #c83c4d;
  font-size: 12px;
}

.feedback-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  margin-top: 14px;
  padding: 11px 16px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 700;
  background: #253047;
  border: 0;
  border-radius: 11px;
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.feedback-submit:hover:not(:disabled) {
  background: #445aed;
  box-shadow: 0 8px 20px rgba(68, 90, 237, 0.24);
  transform: translateY(-1px);
}

.feedback-submit:disabled {
  cursor: not-allowed;
  opacity: 0.48;
}

.feedback-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 8px 10px;
  text-align: center;
}

.feedback-success-icon {
  width: 54px;
  height: 54px;
  margin-bottom: 14px;
  color: #16855b;
  font-size: 25px;
  background: #e8f7f0;
  border-radius: 50%;
}

.feedback-success p {
  margin: 7px 0 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}

.feedback-toast-enter-active,
.feedback-toast-leave-active {
  transition: opacity 0.25s ease, transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.feedback-toast-enter,
.feedback-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(18px) scale(0.97);
}

@media (max-width: 575.98px) {
  .feedback-toast-card {
    bottom: 16px;
    padding: 20px;
    border-radius: 16px;
  }

  .feedback-star {
    flex: 1 1 0;
    width: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .feedback-toast-enter-active,
  .feedback-toast-leave-active,
  .feedback-toast-close,
  .feedback-star,
  .feedback-submit {
    transition: none;
  }
}
</style>
